import {
  Component,
  ComponentRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { DropdownOption } from '../../../../models/dropdown-option.model';
import { CommonService } from '../../../shared/services/common.service';
import { WeightChartComponent } from '../components/weight-chart/weight-chart.component';
import { HeightChartComponent } from '../components/height-chart/height-chart.component';
import { RedLightFoodChartComponent } from '../components/red-light-food-chart/red-light-food-chart.component';
import { PhysicalActivityChartComponent } from '../components/physical-activity-chart/physical-activity-chart.component';
import { ScreenTimeChartComponent } from '../components/screen-time-chart/screen-time-chart.component';
import { JournalingChartComponent } from '../components/journaling-chart/journaling-chart.component';
import { BmiChartComponent } from '../components/bmi-chart/bmi-chart.component';
import { pluck, Subject, takeUntil } from 'rxjs';
import { ChartBaseComponent } from '../components/base/chart-base/chart-base.component';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../state/app.state';
import {
  profile,
  selectedUserProfile,
} from '../../../shared/core/shared.selectors';
import { roles } from 'src/app/modules/shared/constants/common.constants';
import { ProfileModel } from '../../../../models/profile.model';

@Component({
  selector: 'app-progress-screen',
  templateUrl: './progress-screen.component.html',
  styleUrls: ['./progress-screen.component.css'],
})
export class ProgressScreenComponent {
  @ViewChild('chartContainer', { static: true, read: ViewContainerRef })
  chartContainer: ViewContainerRef;
  form = this.formBuilder.group({
    chart: new FormControl<number>(null),
    fromDate: new FormControl<string>(
      moment().subtract(1, 'weeks').toISOString()
    ),
    toDate: new FormControl<string>(moment().toISOString()),
  });
  showDisclaimerBlock: boolean = false;
  dateConfig = {
    fromDate: {
      min: null,
      max: moment().toISOString(),
    },
    toDate: {
      max: moment().toISOString(),
    },
  };
  errorMessages = {};
  errorMessageMap = {
    chart: {},
  };
  components = {
    weightChart: 1,
    heightChart: 2,
    redLightFoodChart: 3,
    physicalActivityChart: 4,
    screenTimeChart: 5,
    journalingChart: 6,
    bmiChart: 7,
  };
  chartOptionsForOwn: DropdownOption[] = [
    {
      key: this.components.weightChart,
      value: 'My Weight',
    },
    {
      key: this.components.heightChart,
      value: 'My Height',
    },
    {
      key: this.components.redLightFoodChart,
      value: 'My Red Light Foods',
    },
    {
      key: this.components.physicalActivityChart,
      value: 'My Physical Activity',
    },
    {
      key: this.components.screenTimeChart,
      value: 'My Screen Time',
    },
    {
      key: this.components.journalingChart,
      value: 'My Journaling',
    },
    {
      key: this.components.bmiChart,
      value: 'My BMI',
    },
  ];
  chartOptionsForOther: DropdownOption[] = [
    {
      key: this.components.weightChart,
      value: 'Weight',
    },
    {
      key: this.components.heightChart,
      value: 'Height',
    },
    {
      key: this.components.redLightFoodChart,
      value: 'Red Light Foods',
    },
    {
      key: this.components.physicalActivityChart,
      value: 'Physical Activity',
    },
    {
      key: this.components.screenTimeChart,
      value: 'Screen Time',
    },
    {
      key: this.components.journalingChart,
      value: 'Journaling',
    },
    {
      key: this.components.bmiChart,
      value: 'BMI',
    },
  ];
  componentList = {
    [this.components.weightChart]: WeightChartComponent,
    [this.components.heightChart]: HeightChartComponent,
    [this.components.redLightFoodChart]: RedLightFoodChartComponent,
    [this.components.physicalActivityChart]: PhysicalActivityChartComponent,
    [this.components.screenTimeChart]: ScreenTimeChartComponent,
    [this.components.journalingChart]: JournalingChartComponent,
    [this.components.bmiChart]: BmiChartComponent,
  };
  currentCharInstance: ComponentRef<ChartBaseComponent>;
  isPrinting: boolean = false;
  memberId: number;
  groupId: number;
  groupStart: string;
  currentUserProfile?: ProfileModel;
  private readonly onDestroy: Subject<any> = new Subject<any>();
  private skipMinDateChange: boolean;
  private initialDataLoaded = false;
  private addingChartRestrictions = false;
  private gotDataExplicitly = false;

  isInitial = true;

  constructor(
    private formBuilder: FormBuilder,
    private commonService: CommonService,
    private route: ActivatedRoute,
    private store: Store<fromRoot.State>,
    private router: Router
  ) {
    this.form.controls['chart'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((value) => {
        this.renderComponent(value);
      });
    this.form.controls['fromDate'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.skipMinDateChange) {
          this.skipMinDateChange = false;
          return;
        }
        this.handleDateChange();
      });
    this.form.controls['toDate'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.handleDateChange();
      });
  }

  ngOnInit() {
    // get member id if this screen is accessing by another role
    this.memberId = +this.route.snapshot.paramMap.get('memberId');

    this.commonService.onNavigationEnd
      .pipe(takeUntil(this.onDestroy))
      .subscribe((d) => {
        const urlParams = {
          params: this.route.snapshot.params,
          queryParams: this.route.snapshot.queryParams,
        };

        const prevUrlParams = this.commonService.urlParams.getValue();

        if (
          d.isRouteChanged ||
          JSON.stringify(prevUrlParams) !== JSON.stringify(urlParams)
        ) {
          this.commonService.urlParams.next(urlParams);
        }
      });

    this.store
      .select(profile)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((profileData: any) => {
        if (
          profileData &&
          profileData.role === roles.adult &&
          !this.route.snapshot.paramMap.get('memberId')
        ) {
          const filteredData = profileData.participants.filter(
            (participant) =>
              !(
                !participant.groupId ||
                !participant.groupStatusKey ||
                participant.groupStatusKey === 1
              )
          );
          if (filteredData.length === 0) {
            this.showDisclaimerBlock = true;
          }
        }
        if (profileData && !this.memberId) {
          if (
            profileData.role === roles.participant &&
            !profileData['groupId']
          ) {
            this.showDisclaimerBlock = true;
            return;
          }
          this.setCurrentProfile(profileData);
        }
      });

    this.store
      .select(selectedUserProfile)
      .pipe(takeUntil(this.onDestroy), pluck('profile'))
      .subscribe((profileData: any) => {
        if (
          profileData &&
          profileData?.role === roles.adult &&
          this.route.snapshot.paramMap.get('memberId')
        ) {
          const filteredData = profileData.participants.filter(
            (participant) =>
              !(
                !participant.groupId ||
                !participant.groupStatusKey ||
                participant.groupStatusKey === 1
              )
          );
          if (filteredData.length === 0) {
            this.showDisclaimerBlock = true;
          }
        }
        if (profileData && this.memberId) {
          if (
            profileData.role === roles.participant &&
            !profileData['groupId']
          ) {
            this.showDisclaimerBlock = true;
            return;
          }
          this.setCurrentProfile(profileData);
        }
      });
  }

  setCurrentProfile(profileData?: ProfileModel) {
    this.currentUserProfile = profileData;

    // render chart requested from url params
    const queryParams = this.route.snapshot.queryParams;
    this.form.patchValue({
      ...queryParams,
      chart: +queryParams['chart'] || this.components.weightChart,
    });

    if (queryParams['fromDate']) {
      this.gotDataExplicitly = true;
    }

    this.groupId =
      profileData.role === roles.participant ? profileData.groupId : -1;

    this.initialDataLoaded = true;

    this.renderComponent(this.form.controls.chart.value);
  }

  async addRestrictionForChart(id: number) {
    // starting adding restriction
    this.addingChartRestrictions = true;

    let fromDate: string = moment().subtract(1, 'weeks').toISOString();
    let minDate: string;

    // restrict start date for group dependent charts
    if (
      [roles.participant, roles.adult].includes(
        this.currentUserProfile?.role
      ) &&
      this.currentUserProfile?.groupStart
    ) {
      fromDate = this.currentUserProfile?.groupStart;
      minDate = this.currentUserProfile?.groupStart;
    } else if (this.currentUserProfile?.dateOfFirstEntryForBT) {
      fromDate = this.currentUserProfile?.dateOfFirstEntryForBT;
      minDate = this.currentUserProfile?.dateOfFirstEntryForBT;
    }

    // this.currentCharInstance.instance.groupId = this.groupId;

    // set fromDate min date to group start date
    // this.setGroupStartDate(this.currentUserProfile?.groupStart);

    // reset previous min
    this.dateConfig.fromDate.min = null;

    if (minDate) {
      this.dateConfig.fromDate.min = moment(minDate).format('YYYY-MM-DD');

      // reset from date if it's less than start date
      if (moment(fromDate).diff(moment(minDate)) < 0) {
        fromDate = moment(this.dateConfig.fromDate.min).toISOString();
      }
    }

    // set from date
    if (!this.gotDataExplicitly && this.isInitial) {
      this.form.patchValue({ fromDate });
      this.form.patchValue({ toDate: moment().toISOString() });
      this.isInitial = false;
    } else {
      this.gotDataExplicitly = false;
    }

    await this.commonService.wait(500);

    // done adding restrictions
    this.addingChartRestrictions = false;
  }

  async renderComponent(id: number) {
    if (!this.initialDataLoaded) {
      // initial data not loaded yet
      return;
    }
    if (this.addingChartRestrictions) {
      // skip render which adding chart restrictions
      return;
    }

    // add chart restrictions
    await this.addRestrictionForChart(id);

    this.currentCharInstance?.destroy();
    this.chartContainer.clear();

    const component: any = this.componentList[id];
    this.currentCharInstance = this.chartContainer.createComponent(component);
    this.currentCharInstance.instance.memberId = this.memberId;
    this.currentCharInstance.instance.userRole = this.currentUserProfile?.role;
    this.currentCharInstance.instance.isAdult = this.currentUserProfile?.role == roles.adult;
    this.currentCharInstance.instance.profile = this.currentUserProfile;
    this.currentCharInstance.instance.chartName =
      this.chartOptionsForOther.find((item) => item.key == id).value;
    this.currentCharInstance.instance.groupId = this.groupId;

    this.handleDateChange();
  }

  handleDateChange() {
    if (!this.initialDataLoaded) {
      // initial data not loaded yet
      return;
    }
    if (this.addingChartRestrictions) {
      // skip render which adding chart restrictions
      return;
    }
    this.currentCharInstance.instance.onDateChange({
      fromDate: this.form.controls['fromDate'].value?.toString(),
      toDate: this.form.controls['toDate'].value?.toString(),
    });
  }

  handlePrint() {
    this.isPrinting = true;
    this.currentCharInstance.instance.onPrint(() => {
      this.isPrinting = false;
    });
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...this.commonService.checkFormValidation(
        this.form,
        this.errorMessageMap,
        currentField
      ),
    };
  }

  getActiveOrLastGroup(groupList) {
    if ((groupList || []).length) {
      this.groupId = groupList[0].groupId;
      this.groupStart = groupList[0].groupStart;
      if (this.groupStart) {
        this.form.controls['fromDate'].patchValue(this.groupStart);
      }
    }
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  weightStartDate(dateOfFirstWeightEnteredByCoach) {
    if (dateOfFirstWeightEnteredByCoach) {
      this.skipMinDateChange = true;
      this.dateConfig.fromDate.min = moment(
        dateOfFirstWeightEnteredByCoach
      ).format('YYYY-MM-DD');

      // reset from date if it's less than first weight date
      if (
        moment(this.form.controls.fromDate.value).diff(
          moment(this.dateConfig.fromDate.min)
        ) < 0
      ) {
        this.form.patchValue(
          {
            fromDate: moment(this.dateConfig.fromDate.min).toISOString(),
          },
          {
            emitEvent: false,
          }
        );
      } else {
        // call the initial date change function
        // this.handleDateChange();
      }
    } else {
      // call the initial date change function
      // this.handleDateChange();
    }
  }
}
